if (!customElements.get('shed-coupon')) {
  customElements.define(
    'shed-coupon',
    class ShedCoupon extends HTMLElement {
      connectedCallback() {
        this.code = this.dataset.code;
        this.percent = Number(this.dataset.percent) || 0;
        this.shed = this.querySelector('[data-sc-shed]');
        this.live = this.querySelector('[data-sc-live]');
        this.status = this.querySelector('[data-sc-status]');
        this.peekTimers = [];

        this.shed.addEventListener('click', () => this.open());
        this.querySelector('[data-sc-reveal]')?.addEventListener('click', () => this.open());
        this.querySelector('[data-sc-copy]').addEventListener('click', (e) => this.copy(e.currentTarget));
        this.querySelector('[data-sc-apply]').addEventListener('click', (e) => this.apply(e.currentTarget));
        this.initPeek();
        this.initVariantUpdates();
      }

      disconnectedCallback() {
        this.stopPeek();
        this.peekObserver?.disconnect();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      open() {
        if (this.classList.contains('is-open')) return;
        this.stopPeek();
        this.classList.add('is-open');
        this.shed.setAttribute('aria-expanded', 'true');
        this.shed.setAttribute('aria-disabled', 'true');
        this.querySelector('[data-sc-closed]').hidden = true;
        this.querySelector('[data-sc-open]').hidden = false;
        this.querySelector('[data-sc-code]').focus({ preventScroll: true });
        this.announce(`Your code is ${this.code}`);
      }

      // Three quick peeks once the box is mostly on screen, then stop for good on this page view.
      initPeek() {
        if (this.dataset.peek !== 'true') return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        if (!('IntersectionObserver' in window)) return;
        this.peekObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.peekObserver.disconnect();
            [1000, 5500, 10000].forEach((delay) => this.peekTimers.push(setTimeout(() => this.peek(), delay)));
          },
          { threshold: 0.6 }
        );
        this.peekObserver.observe(this);
      }

      // Web Animations plays a fresh run every call; re-adding a CSS class doesn't reliably replay on SVG groups (Safari).
      peek() {
        if (this.classList.contains('is-open')) return;
        const doors = this.querySelectorAll('.shed-coupon__door--left, .shed-coupon__door--right');
        if (!doors.length || typeof doors[0].animate !== 'function') {
          this.classList.remove('is-peeking');
          void this.offsetWidth;
          this.classList.add('is-peeking');
          this.peekTimers.push(setTimeout(() => this.classList.remove('is-peeking'), 650));
          return;
        }
        this.peekAnimations = [...doors].map((door) =>
          door.animate(
            [
              { transform: 'scaleX(1)' },
              { transform: 'scaleX(0.84)', offset: 0.3 },
              { transform: 'scaleX(1)', offset: 0.6 },
              { transform: 'scaleX(1)' },
            ],
            { duration: 550, easing: 'ease-out' }
          )
        );
        this.peekCount = (this.peekCount || 0) + 1;
      }

      stopPeek() {
        this.peekTimers.forEach(clearTimeout);
        this.peekTimers = [];
        this.peekAnimations?.forEach((animation) => animation.cancel());
        this.classList.remove('is-peeking');
      }

      initVariantUpdates() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const { sectionId, variant } = event.data || {};
          if (sectionId !== this.dataset.section) return;
          // No variant means the chosen option combination doesn't exist, so it can't be bought either.
          if (this.dataset.hideSoldOut === 'true') this.hidden = !variant || !variant.available;
          if (!variant) return;
          this.updateSavings(variant.price);
        });
      }

      updateSavings(priceCents) {
        const savings =
          this.dataset.type === 'fixed_amount'
            ? Math.min(Number(this.dataset.amountOff) || 0, priceCents)
            : Math.round((priceCents * this.percent) / 100);
        this.querySelectorAll('[data-sc-amount]').forEach((el) => {
          el.textContent = this.formatMoney(savings);
        });
      }

      // Whole-dollar amounts drop ".00", like money_without_trailing_zeros.
      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const decimals = cents % 100 === 0 ? 0 : 2;
        const fmt = (value, places, thousands = ',', decimal = '.') => {
          const [whole, frac] = (value / 100).toFixed(places).split('.');
          return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
        };
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
          switch (key) {
            case 'amount_no_decimals':
              return fmt(cents, 0);
            case 'amount_with_comma_separator':
              return fmt(cents, decimals, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return fmt(cents, 0, '.');
            case 'amount_with_apostrophe_separator':
              return fmt(cents, decimals, "'");
            default:
              return fmt(cents, decimals);
          }
        });
      }

      async copy(button) {
        try {
          await navigator.clipboard.writeText(this.code);
        } catch {
          const input = document.createElement('input');
          input.value = this.code;
          input.setAttribute('readonly', '');
          input.style.position = 'absolute';
          input.style.left = '-9999px';
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
        }
        const label = button.textContent;
        button.textContent = this.dataset.copiedText || 'Copied!';
        this.announce(this.dataset.copiedText || 'Copied!');
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => (button.textContent = label), 2000);
      }

      // Shopify's /discount/CODE link stores the code on the session; it is applied at checkout.
      async apply(button) {
        const url = `${window.Shopify?.routes?.root || '/'}discount/${encodeURIComponent(this.code)}`;
        button.disabled = true;
        try {
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) throw new Error(response.status);
          this.status.textContent = this.dataset.appliedText;
          this.announce(this.dataset.appliedText);
        } catch {
          window.location.href = `${url}?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;
        }
      }

      announce(message) {
        this.live.textContent = '';
        requestAnimationFrame(() => (this.live.textContent = message));
      }
    }
  );
}
